import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { getConsent } from "./CookieConsent";

/**
 * PreStreamAd — Interstitiel publicitaire affiché avant qu'un track ne démarre.
 *
 * - Ne se déclenche que si (a) Monetag activé + zone `prestream` configurée
 *   côté admin, (b) consentement pub OK, (c) l'utilisateur n'a pas vu de pub
 *   pré-stream depuis N secondes (frequency capping, défaut 5 min).
 * - Countdown configurable côté admin (défaut 5 sec).
 * - onComplete est appelé QUAND le countdown atteint 0 → le player peut lancer
 *   la lecture. onClose = fermeture prématurée (skip après 3s minimum).
 *
 * Props:
 *   open, onComplete, onClose, zoneId, duration
 */
const CAP_MS_KEY = "mboa_prestream_last";
const CAP_MS = 5 * 60 * 1000; // 5 min entre 2 pré-streams

export function shouldShowPreStream() {
    try {
        const consent = getConsent();
        if (!consent?.advertising) return false;
        const last = parseInt(localStorage.getItem(CAP_MS_KEY) || "0", 10);
        if (Date.now() - last < CAP_MS) return false;
        return true;
    } catch { return false; }
}

export function markPreStreamShown() {
    try { localStorage.setItem(CAP_MS_KEY, String(Date.now())); } catch { /* no-op */ }
}

export default function PreStreamAd({ open, onComplete, onClose, zoneId, duration = 5 }) {
    const [remaining, setRemaining] = useState(duration);

    useEffect(() => {
        if (!open) return;
        setRemaining(duration);
        const t = setInterval(() => {
            setRemaining((r) => {
                if (r <= 1) {
                    clearInterval(t);
                    onComplete?.();
                    return 0;
                }
                return r - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [open, duration, onComplete]);

    if (!open) return null;

    const canSkip = remaining <= Math.max(0, duration - 3);

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="prestream-ad"
        >
            <div className="relative w-full max-w-2xl bg-[#0d0d0d] border border-[#262626] rounded-2xl overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
                    <span className="text-[10px] uppercase tracking-widest text-[#00c0ff] font-bold">Publicité</span>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-[#A8A8A8]">
                            {remaining > 0 ? `Ta musique dans ${remaining}s…` : "C'est parti !"}
                        </span>
                        {canSkip && (
                            <button
                                onClick={onClose}
                                className="text-[#A8A8A8] hover:text-[#F4F4F4] p-1"
                                data-testid="prestream-skip"
                                aria-label="Passer"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                <div
                    className="min-h-[280px] flex items-center justify-center bg-[#0a0a0a]"
                    data-zone={zoneId}
                    data-testid="prestream-slot"
                >
                    <p className="text-[#4A4A4A] text-xs">Ta pub s'affiche ici…</p>
                </div>
                <div className="h-1 bg-[#161616] overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-[#00d97e] to-[#ffc83d] transition-all duration-1000 ease-linear"
                        style={{ width: `${((duration - remaining) / Math.max(1, duration)) * 100}%` }}
                    />
                </div>
            </div>
        </div>
    );
}
